// client/src/components/admin/league-tabs/MatchManagementTab.js
import React, { useMemo } from 'react'

/**
 * Get CSS class for match status badge
 * @param {string} status - Match status
 * @returns {string} Bootstrap badge class
 */
const getStatusBadge = (status) => {
  switch (status) {
    case 'scheduled':
      return 'bg-info'
    case 'normal':
    case 'opponent_retired':
    case 'i_retired':
    case 'home_retired':
    case 'away_retired':
      return 'bg-success'
    default:
      return 'bg-secondary'
  }
}

/**
 * Get formatted text for match status
 * @param {string} status - Match status
 * @returns {string} Human-readable status text
 */
const getStatusText = (status) => {
  switch (status) {
    case 'scheduled':
      return 'Scheduled'
    case 'normal':
      return 'Completed'
    case 'opponent_retired':
      return 'Opponent Retired'
    case 'i_retired':
      return 'Player Retired'
    case 'home_retired':
      return 'Home Retired'
    case 'away_retired':
      return 'Away Retired'
    default:
      return 'Unscheduled'
  }
}

/**
 * Format date for display
 * @param {string} dateStr - ISO date string
 * @returns {string} Formatted date or em dash
 */
const formatDate = (dateStr) => {
  if (!dateStr) return '—'
  try {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    })
  } catch {
    return '—'
  }
}

/**
 * MatchManagementTab - Displays and manages all league matches
 *
 * @param {object} props
 * @param {array} props.matches - Array of match objects
 * @param {Map} props.playersById - Map of player_id to player object
 * @param {Map} props.divisionsById - Map of division_id to division object
 * @param {function} props.onDeleteMatch - Handler for deleting invalid matches
 * @param {function} props.onSwapHomeAway - Handler for swapping home/away players
 * @param {function} props.navigate - React Router navigate function
 */
export default function MatchManagementTab({
  matches,
  playersById,
  divisionsById,
  onDeleteMatch,
  onSwapHomeAway,
  navigate,
}) {
  // Compute match data with division information
  const enrichedMatches = useMemo(() => {
    return matches.map((match) => {
      const home = playersById.get(match.home_player_id)
      const away = playersById.get(match.away_player_id)

      // Get division names for home and away players
      const homeDivisionId = home?.division_id || null
      const awayDivisionId = away?.division_id || null

      const homeDivisionName = homeDivisionId
        ? divisionsById.get(homeDivisionId)?.division_name || 'Unknown'
        : 'None'

      const awayDivisionName = awayDivisionId
        ? divisionsById.get(awayDivisionId)?.division_name || 'Unknown'
        : 'None'

      // Check if match is invalid (cross-division)
      const isInvalidMatch =
        !homeDivisionId || !awayDivisionId || homeDivisionId !== awayDivisionId

      // Check if match is completed (cannot swap)
      const isCompletedMatch = [
        'normal',
        'opponent_retired',
        'i_retired',
        'home_retired',
        'away_retired',
      ].includes(match.status)

      // Safely derive player names (guards against missing players)
      const player1Name = home
        ? `${home.first_name || ''} ${home.last_name || ''}`.trim() ||
          'Unknown Player'
        : 'Unknown Player'
      const player2Name = away
        ? `${away.first_name || ''} ${away.last_name || ''}`.trim() ||
          'Unknown Player'
        : 'Unknown Player'

      return {
        ...match,
        homeDivisionName,
        awayDivisionName,
        player1Name,
        player2Name,
        isInvalidMatch,
        isCompletedMatch,
      }
    })
  }, [matches, playersById, divisionsById])

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h4>
          <i className="fas fa-tennis-ball me-2" aria-hidden="true"></i>
          League Matches
        </h4>
        <span
          className="badge bg-info"
          aria-label={`${matches.length} matches`}
        >
          {matches.length} {matches.length === 1 ? 'match' : 'matches'}
        </span>
      </div>

      {matches.length === 0 ? (
        <div className="text-center py-5">
          <i
            className="fas fa-tennis-ball fa-3x text-muted mb-3"
            aria-hidden="true"
          ></i>
          <h5 className="text-muted">No matches found</h5>
          <p className="text-muted">
            Matches will appear here once divisions are created and players are
            assigned.
          </p>
        </div>
      ) : (
        <div className="table-responsive">
          <table className="table table-hover">
            <thead>
              <tr>
                <th scope="col">Home Division</th>
                <th scope="col">Away Division</th>
                <th scope="col">Players (away v home)</th>
                <th scope="col">Status</th>
                <th scope="col">Scheduled Date</th>
                <th scope="col">Score</th>
                <th scope="col">Actions</th>
              </tr>
            </thead>
            <tbody>
              {enrichedMatches.map((match) => (
                <tr
                  key={match.id}
                  className={match.isInvalidMatch ? 'table-danger' : ''}
                >
                  <td>
                    {match.isInvalidMatch && (
                      <i
                        className="fas fa-exclamation-triangle text-danger me-2"
                        title="Invalid cross-division match"
                        aria-label="Warning: Invalid cross-division match"
                      ></i>
                    )}
                    {match.homeDivisionName}
                  </td>
                  <td>
                    {match.isInvalidMatch && (
                      <i
                        className="fas fa-exclamation-triangle text-danger me-2"
                        title="Invalid cross-division match"
                        aria-label="Warning: Invalid cross-division match"
                      ></i>
                    )}
                    {match.awayDivisionName}
                  </td>
                  <td>
                    {match.player2Name} vs {match.player1Name}
                  </td>
                  <td>
                    <span className={`badge ${getStatusBadge(match.status)}`}>
                      {getStatusText(match.status)}
                    </span>
                  </td>
                  <td>{formatDate(match.match_datetime)}</td>
                  <td>
                    {match.isCompletedMatch ? (
                      <span className="text-success">
                        <i
                          className="fas fa-check-circle me-1"
                          aria-hidden="true"
                        ></i>
                        Recorded
                      </span>
                    ) : (
                      <span className="text-muted">—</span>
                    )}
                  </td>
                  <td>
                    <div
                      className="btn-group"
                      role="group"
                      aria-label="Match actions"
                    >
                      <button
                        className="btn btn-sm btn-outline-primary"
                        onClick={() =>
                          navigate(
                            `/leagues/${match.league_id}/match/${match.id}/score`,
                          )
                        }
                        title="View/Edit match score (Admin Override)"
                        data-track="admin.edit_match_score"
                      >
                        <i className="fas fa-edit me-1" aria-hidden="true"></i>
                        {match.isCompletedMatch ? 'Edit Score' : 'Enter Score'}
                      </button>
                      {match.status === 'scheduled' && match.match_datetime && (
                        <button
                          className="btn btn-sm btn-outline-secondary"
                          onClick={() =>
                            navigate(
                              `/leagues/${match.league_id}/match/${match.id}/schedule`,
                            )
                          }
                          title="Reschedule match"
                          data-track="admin.reschedule_match"
                        >
                          <i
                            className="fas fa-calendar me-1"
                            aria-hidden="true"
                          ></i>
                          Reschedule
                        </button>
                      )}
                      {!match.isCompletedMatch && (
                        <button
                          className="btn btn-sm btn-outline-info"
                          onClick={() => onSwapHomeAway(match.id)}
                          title="Swap home and away players"
                          data-track="admin.swap_home_away"
                        >
                          <i
                            className="fas fa-exchange-alt me-1"
                            aria-hidden="true"
                          ></i>
                          Swap
                        </button>
                      )}
                      {match.isInvalidMatch && (
                        <button
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => onDeleteMatch(match.id)}
                          title="Delete invalid cross-division match"
                          data-track="admin.delete_invalid_match"
                        >
                          <i
                            className="fas fa-trash me-1"
                            aria-hidden="true"
                          ></i>
                          Delete
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  )
}
